import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { formatErrorDetail } from "@/lib/format-error";
import { STUCK_SENDING_REASON, stuckSendNote } from "@/lib/stuck-send";
import Sidebar from "@/components/Sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Mail, Send, RefreshCw, Users as UsersIcon, Workflow, Pause, Play, X, Plus, RotateCcw, AlertCircle, Eye, MousePointerClick, FileText } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }) => {
  if (status === "delivered") {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">delivered</Badge>;
  }
  if (status === "sent") {
    return <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">sent</Badge>;
  }
  if (status === "delayed") {
    return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">delayed</Badge>;
  }
  if (status === "bounced") {
    return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">bounced</Badge>;
  }
  if (status === "complained") {
    return <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100">spam complaint</Badge>;
  }
  if (status === "failed") {
    return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">failed</Badge>;
  }
  return <Badge variant="secondary">{status || "pending"}</Badge>;
};

const ENROL_STATUS_FILTERS = ["active", "paused", "completed", "cancelled", "all"];

// Cancels the backend's Retry undoes (_is_retryable_cancel): the scheduler gave
// up on a step because Resend kept rejecting it, or its template was deleted.
const isRetryableCancel = (e) =>
  e.status === "cancelled" &&
  ((e.cancelled_reason || "").startsWith("send_failed") || e.cancelled_reason === "template_deleted");

// The toast for POST /api/enrollments when it doesn't enrol (see _enroll_one).
const describeEnrolSkip = (data) => {
  if (data.status === "enrolled_no_sends") {
    return "Enrolled, but every step's send time has already passed, so nothing will be sent.";
  }
  switch (data.reason) {
    case "already_enrolled_or_completed":
      return "Skipped: this contact is already in this sequence, or has completed it.";
    case "previously_cancelled":
      return `Skipped: this contact's earlier enrolment in this sequence was cancelled (${(data.cancelled_reason || "no reason recorded").split(":")[0]}). Use Retry on that enrolment instead.`;
    case "suppressed":
      return "Skipped: this address is on the do-not-send list (Suppressions).";
    case "opted_out":
      return "Skipped: this contact has opted out of email in HubSpot.";
    case "not_marketable":
      return "Skipped: this contact isn't a marketing contact in HubSpot.";
    default:
      return `Skipped: ${data.reason || data.status}`;
  }
};

const EmailTools = ({ user, setUser }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [sending, setSending] = useState(false);

  // One-off send: type recipient + pick template.
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  // HubSpot lists (shared across all list-pickers on this page)
  const [hubspotLists, setHubspotLists] = useState([]);

  // Recent sends
  const [tips, setTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [errorDialog, setErrorDialog] = useState(null); // {recipient, error} | null
  const [timelineDialog, setTimelineDialog] = useState(null); // {sendRow, events, loading} | null
  const [previewOpen, setPreviewOpen] = useState(false);

  // Enrolments tab state (Phase 3b). ?tab=enrolments&status=paused deep-links
  // here (the stuck-send notification uses it); &sequence=<id> also scopes the
  // list to one sequence (the failed-sends notification uses it).
  const [searchParams] = useSearchParams();
  const [activeTopTab, setActiveTopTab] = useState(searchParams.get("tab") === "enrolments" ? "enrolments" : "send"); // "send" | "enrolments"
  const [enrollments, setEnrollments] = useState([]);
  const [enrolFilter, setEnrolFilter] = useState(
    ENROL_STATUS_FILTERS.includes(searchParams.get("status")) ? searchParams.get("status") : "active"
  );
  const [enrolSequenceFilter, setEnrolSequenceFilter] = useState(searchParams.get("sequence") || "all"); // sequence id | "all"
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [sequences, setSequences] = useState([]);
  // Retry: one cancelled enrolment, or every failed one in the scoped sequence
  const [retryEnrolment, setRetryEnrolment] = useState(null); // enrolment row | null
  const [retryFailedOpen, setRetryFailedOpen] = useState(false);
  const [retryingFailed, setRetryingFailed] = useState(false);
  // Enrol-contact modal
  const [enrolDialogOpen, setEnrolDialogOpen] = useState(false);
  const [enrolSequenceId, setEnrolSequenceId] = useState("");
  const [enrolMode, setEnrolMode] = useState("hubspot");
  const [enrolListId, setEnrolListId] = useState("");
  const [enrolContacts, setEnrolContacts] = useState([]);
  const [enrolTotalMembers, setEnrolTotalMembers] = useState(0);
  const [enrolSelectedContactId, setEnrolSelectedContactId] = useState("");
  const [enrolEmail, setEnrolEmail] = useState("");
  const [enrolName, setEnrolName] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  // Bulk-enrol modal
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkSequenceId, setBulkSequenceId] = useState("");
  const [bulkListId, setBulkListId] = useState("");
  const [bulking, setBulking] = useState(false);

  const fetchTips = async () => {
    try {
      const res = await axios.get(`${API}/email-sends?limit=10`, {
        withCredentials: true,
        headers: authHeaders,
      });
      setTips(res.data.sends || res.data.tips || []);
    } catch (err) {
      console.error("Failed to fetch email sends:", err);
      toast.error("Failed to load recent sends");
    } finally {
      setLoadingTips(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/email-templates`, {
        withCredentials: true,
        headers: authHeaders,
      });
      setTemplates(res.data.templates || []);
    } catch (err) {
      // silent — templates page will surface errors if needed
    }
  };

  const fetchHubspotLists = async () => {
    try {
      const res = await axios.get(`${API}/hubspot/lists`, { params: { allowed_only: true }, withCredentials: true, headers: authHeaders });
      const lists = res.data.lists || [];
      setHubspotLists(lists);
      const def = res.data.default_list_id || "";
      // Pre-fill list-id pickers if currently empty
      setEnrolListId((prev) => prev || def);
      setBulkListId((prev) => prev || def);
    } catch (err) { /* silent — pickers will show "no lists loaded" */ }
  };

  const fetchSequences = async () => {
    try {
      const res = await axios.get(`${API}/sequences`, { withCredentials: true, headers: authHeaders });
      setSequences(res.data.sequences || []);
    } catch (err) { /* silent */ }
  };

  const fetchEnrollments = async (status = enrolFilter, sequenceId = enrolSequenceFilter) => {
    setLoadingEnrollments(true);
    try {
      const params = status && status !== "all" ? { status } : {};
      if (sequenceId && sequenceId !== "all") params.sequence_id = sequenceId;
      const res = await axios.get(`${API}/enrollments`, { params, withCredentials: true, headers: authHeaders });
      setEnrollments(res.data.enrollments || []);
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load enrolments"));
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleEnrolmentAction = async (enrollmentId, action) => {
    try {
      await axios.put(`${API}/enrollments/${enrollmentId}`, { action }, { withCredentials: true, headers: authHeaders });
      toast.success(`Enrolment ${action}d`);
      fetchEnrollments();
    } catch (err) {
      toast.error(formatErrorDetail(err, `${action} failed`));
    }
  };

  // The sequence the list is scoped to, when it is (and it still exists).
  const scopedSequence = enrolSequenceFilter !== "all" ? sequences.find((s) => s.id === enrolSequenceFilter) : null;

  const handleRetryEnrolment = async (enr) => {
    try {
      await axios.put(`${API}/enrollments/${enr.id}`, { action: "retry" }, { withCredentials: true, headers: authHeaders });
      toast.success(`${enr.recipient_email} resumes at the step that failed`);
      fetchEnrollments();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Retry failed"));
    }
  };

  const handleRetryFailed = async () => {
    if (!scopedSequence) return;
    setRetryingFailed(true);
    try {
      const res = await axios.post(`${API}/sequences/${scopedSequence.id}/retry-failed`, {}, { withCredentials: true, headers: authHeaders });
      const { retried, skipped } = res.data;
      if (!retried && !skipped) {
        toast.info("No failed sends to retry in this sequence");
      } else {
        toast.success(
          `Retried ${retried} enrolment${retried === 1 ? "" : "s"}` +
            (skipped ? `; skipped ${skipped} (enrolled again since, or the step's template is missing)` : ""),
          { duration: 6000 },
        );
      }
      fetchEnrollments();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Couldn't retry the failed sends"));
    } finally {
      setRetryingFailed(false);
    }
  };

  const handleEnrolContactsLoad = async () => {
    setEnrolContacts([]); setEnrolTotalMembers(0); setEnrolSelectedContactId("");
    try {
      const params = enrolListId.trim() ? { list_id: enrolListId.trim() } : {};
      const res = await axios.get(`${API}/hubspot/list-contacts`, { params, withCredentials: true, headers: authHeaders });
      setEnrolContacts(res.data.contacts || []);
      setEnrolTotalMembers(res.data.total_members || 0);
    } catch (err) {
      toast.error(formatErrorDetail(err));
    }
  };

  const handleEnrolSubmit = async () => {
    if (!enrolSequenceId) { toast.error("Pick a sequence"); return; }
    const payload = { sequence_id: enrolSequenceId };
    if (enrolMode === "hubspot") {
      if (!enrolSelectedContactId) { toast.error("Pick a contact"); return; }
      payload.hubspot_contact_id = enrolSelectedContactId;
    } else {
      if (!enrolEmail.trim() || !enrolName.trim()) { toast.error("Email and name required"); return; }
      payload.recipient_email = enrolEmail.trim();
      payload.recipient_name = enrolName.trim();
    }
    setEnrolling(true);
    try {
      const res = await axios.post(`${API}/enrollments`, payload, { withCredentials: true, headers: authHeaders });
      if (res.data.status === "enrolled") {
        // next_send_at is null for now (parallel branches each have their own)
        toast.success(res.data.next_send_at ? `Enrolled — first email at ${new Date(res.data.next_send_at).toLocaleString()}` : "Enrolled");
      } else {
        toast.warning(describeEnrolSkip(res.data), { duration: 8000 });
      }
      setEnrolDialogOpen(false);
      fetchEnrollments();
    } catch (err) {
      toast.error(formatErrorDetail(err));
    } finally { setEnrolling(false); }
  };

  const handleBulkSubmit = async () => {
    if (!bulkSequenceId) { toast.error("Pick a sequence"); return; }
    setBulking(true);
    try {
      const body = bulkListId.trim() ? { list_id: bulkListId.trim() } : {};
      const res = await axios.post(`${API}/sequences/${bulkSequenceId}/bulk-enroll`, body, { withCredentials: true, headers: authHeaders });
      toast.success(`Enrolled ${res.data.enrolled}, skipped ${res.data.skipped_opt_out + res.data.skipped_non_marketing + res.data.skipped_already_in + res.data.skipped_other}, errors ${res.data.errors}`, { duration: 6000 });
      setBulkDialogOpen(false);
      fetchEnrollments();
    } catch (err) {
      toast.error(formatErrorDetail(err));
    } finally { setBulking(false); }
  };

  useEffect(() => {
    fetchTips();
    fetchTemplates();
    fetchSequences();
    fetchEnrollments();
    fetchHubspotLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEnrollments(enrolFilter, enrolSequenceFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolFilter, enrolSequenceFilter]);

  // A notification link opened while this page is already showing changes the
  // query string without remounting, so apply it here too.
  useEffect(() => {
    if (searchParams.get("tab") !== "enrolments") return;
    setActiveTopTab("enrolments");
    if (ENROL_STATUS_FILTERS.includes(searchParams.get("status"))) setEnrolFilter(searchParams.get("status"));
    setEnrolSequenceFilter(searchParams.get("sequence") || "all");
  }, [searchParams]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    navigate("/");
  };

  const handleOpenTimeline = async (sendRow) => {
    setTimelineDialog({ sendRow, events: [], meta: null, loading: true });
    try {
      const res = await axios.get(`${API}/email-sends/${sendRow.id}/events`, {
        withCredentials: true, headers: authHeaders,
      });
      setTimelineDialog({ sendRow, events: res.data.events || [], meta: res.data.meta || null, loading: false });
    } catch (err) {
      toast.error(formatErrorDetail(err, "Failed to load timeline"));
      setTimelineDialog(null);
    }
  };

  const handleRetry = async (sendRow) => {
    setRetryingId(sendRow.id);
    try {
      const res = await axios.post(
        `${API}/email-sends/${sendRow.id}/retry`,
        {},
        { withCredentials: true, headers: authHeaders },
      );
      if (res.data.success) {
        toast.success(`Resent — message id: ${res.data.message_id}`);
      } else {
        toast.error(res.data.error || "Retry failed");
      }
      fetchTips();
    } catch (err) {
      toast.error(formatErrorDetail(err, "Retry failed"));
    } finally {
      setRetryingId(null);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplateId) {
      toast.error("Pick a template first");
      return;
    }
    if (!recipientEmail.trim() || !recipientName.trim()) {
      toast.error("Recipient email and name are both required");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/send-from-template`,
        {
          template_id: selectedTemplateId,
          recipient_email: recipientEmail.trim(),
          recipient_name: recipientName.trim(),
        },
        { withCredentials: true, headers: authHeaders },
      );
      if (res.data.success) {
        const extra = res.data.missing_vars?.length ? ` (missing vars: ${res.data.missing_vars.join(", ")})` : "";
        toast.success(`Sent — message id: ${res.data.message_id}${extra}`);
        setRecipientEmail("");
        setRecipientName("");
        fetchTips();
      } else {
        toast.error(res.data.error || "Send failed");
        fetchTips();
      }
    } catch (err) {
      toast.error(formatErrorDetail(err, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="email-tools-page">
      <Sidebar user={user} onLogout={handleLogout} />

      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8 animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Email Tools
            </h1>
            <p className="text-slate-500 mt-1">
              Send a one-off email from a saved template, or enrol contacts into a sequence.
            </p>
          </div>
        </div>

        <Tabs value={activeTopTab} onValueChange={setActiveTopTab} className="mb-8">
          <TabsList>
            <TabsTrigger value="send"><Send className="w-4 h-4 mr-2" /> Send one-off</TabsTrigger>
            <TabsTrigger value="enrolments"><Workflow className="w-4 h-4 mr-2" /> Enrolments ({enrollments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="enrolments" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2"><Workflow className="w-5 h-5 text-indigo-600" /> Sequence enrolments</span>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Select value={enrolSequenceFilter} onValueChange={setEnrolSequenceFilter}>
                      <SelectTrigger className="w-[180px]" data-testid="enrol-sequence-filter"><SelectValue placeholder="All sequences" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sequences</SelectItem>
                        {sequences.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={enrolFilter} onValueChange={setEnrolFilter}>
                      <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => fetchEnrollments()} disabled={loadingEnrollments}>
                      <RefreshCw className={`w-4 h-4 ${loadingEnrollments ? "animate-spin" : ""}`} />
                    </Button>
                    {scopedSequence && (enrolFilter === "cancelled" || enrolFilter === "all") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRetryFailedOpen(true)}
                        disabled={retryingFailed}
                        title="Resume every enrolment in this sequence whose send failed, at the step that failed"
                        data-testid="retry-failed-sends"
                      >
                        <RotateCcw className={`w-4 h-4 mr-1 ${retryingFailed ? "animate-spin" : ""}`} /> Retry failed sends
                      </Button>
                    )}
                    <Button onClick={() => { setEnrolDialogOpen(true); setEnrolContacts([]); setEnrolSelectedContactId(""); }} disabled={!sequences.filter(s => s.status === "active").length} variant="secondary" size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Enrol contact
                    </Button>
                    <Button onClick={() => setBulkDialogOpen(true)} disabled={!sequences.filter(s => s.status === "active").length} variant="secondary" size="sm">
                      <UsersIcon className="w-4 h-4 mr-1" /> Bulk enrol from list
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  {sequences.filter(s => s.status === "active").length === 0
                    ? "No active sequences yet — go to Sequences and activate one."
                    : `${sequences.filter(s => s.status === "active").length} active sequence(s) available for enrolment.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingEnrollments ? (
                  <p className="text-slate-500 text-sm">Loading…</p>
                ) : enrollments.length === 0 ? (
                  <p className="text-slate-500 text-sm">No enrolments in this filter.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sequence</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Step</TableHead>
                        <TableHead>Next send</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments.map((e) => (
                        <TableRow key={e.id}>
                          <TableCell className="text-sm">{e.sequence_name}</TableCell>
                          <TableCell className="font-mono text-xs">{e.recipient_email}<div className="text-slate-500 text-xs font-sans">{e.recipient_name}</div></TableCell>
                          <TableCell>
                            <Badge className={
                              e.status === "active" ? "bg-emerald-100 text-emerald-700" :
                              e.status === "paused" ? "bg-amber-100 text-amber-700" :
                              e.status === "completed" ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-700"
                            }>{e.status}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">{(e.current_step_index ?? 0) + 1}</TableCell>
                          <TableCell className="text-slate-500 text-xs">{e.status === "active" ? formatDate(e.next_send_at) : "—"}</TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {e.paused_reason === STUCK_SENDING_REASON ? (
                              <div data-testid={`stuck-send-${e.id}`}>
                                <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">Stuck mid-send</Badge>
                                <div className="mt-1 max-w-[16rem]">{stuckSendNote(e.stuck_send)}</div>
                              </div>
                            ) : (e.cancelled_reason || "—")}
                          </TableCell>
                          <TableCell className="text-right space-x-1">
                            {e.status === "active" && (
                              <Button variant="ghost" size="sm" onClick={() => handleEnrolmentAction(e.id, "pause")} title="Pause"><Pause className="w-4 h-4 text-amber-600" /></Button>
                            )}
                            {e.status === "paused" && (
                              <Button variant="ghost" size="sm" onClick={() => handleEnrolmentAction(e.id, "resume")} title="Resume"><Play className="w-4 h-4 text-emerald-600" /></Button>
                            )}
                            {(e.status === "active" || e.status === "paused") && (
                              <Button variant="ghost" size="sm" onClick={() => handleEnrolmentAction(e.id, "cancel")} title="Cancel" className="text-red-600"><X className="w-4 h-4" /></Button>
                            )}
                            {isRetryableCancel(e) && (
                              <Button variant="ghost" size="sm" onClick={() => setRetryEnrolment(e)} title="Retry from the step that failed" data-testid={`retry-enrolment-${e.id}`}>
                                <RotateCcw className="w-4 h-4 text-indigo-600" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="send" className="mt-4">

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Send a one-off email
            </CardTitle>
            <CardDescription>
              Pick a template, type the recipient, send. For ad-hoc sends and quick smoke tests.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tpl-pick">Template</Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId} disabled={sending}>
                <SelectTrigger id="tpl-pick">
                  <SelectValue placeholder={templates.length ? "Pick a template…" : "No templates yet — create one in Email Templates"} />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="font-medium">{t.name}</span>
                      <span className="text-slate-400 text-xs ml-2">· {t.category} · {t.subject}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tpl-rec-email">Recipient email</Label>
                <Input id="tpl-rec-email" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="you@gmail.com" disabled={sending} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tpl-rec-name">Recipient name</Label>
                <Input id="tpl-rec-name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Dean Loader" disabled={sending} />
              </div>
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || !selectedTemplateId}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="send-template-btn"
            >
              {sending ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send</>}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent sends</span>
              <Button variant="ghost" size="sm" onClick={fetchTips} disabled={loadingTips}>
                <RefreshCw className={`w-4 h-4 ${loadingTips ? "animate-spin" : ""}`} />
              </Button>
            </CardTitle>
            <CardDescription>Last 10 — most recent first</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTips ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : tips.length === 0 ? (
              <p className="text-slate-500 text-sm">No sends yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Opens</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tips.map((t) => {
                    const isFailed = t.status === "failed" || t.status === "bounced" || t.status === "complained";
                    return (
                    <TableRow key={t.id} className={isFailed ? "bg-red-50/50" : ""}>
                      <TableCell className="font-mono text-xs">{t.recipient_email}</TableCell>
                      <TableCell>{t.recipient_name}</TableCell>
                      <TableCell>
                        {t.template_name ? (
                          <span className="text-sm">{t.template_name} <Badge variant="outline" className="ml-1 text-xs">{t.category || "tpl"}</Badge></span>
                        ) : (
                          <span className="text-slate-500 text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-xs">{formatDate(t.sent_at)}</TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleOpenTimeline(t)}
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                          title="Click for full delivery timeline"
                        >
                          <StatusBadge status={t.status} />
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        {t.first_opened_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-blue-600"
                            title={`Last opened ${new Date(t.last_opened_at || t.first_opened_at).toLocaleString()}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium">{t.open_count || 1}</span>
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {t.first_clicked_at ? (
                          <span
                            className="inline-flex items-center gap-1 text-violet-600"
                            title={`Last clicked ${new Date(t.last_clicked_at || t.first_clicked_at).toLocaleString()}${t.last_clicked_url ? `\nURL: ${t.last_clicked_url}` : ""}`}
                          >
                            <MousePointerClick className="w-3.5 h-3.5" />
                            <span className="text-sm font-medium">{t.click_count || 1}</span>
                          </span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        {isFailed && (
                          <div className="flex items-center justify-end gap-1">
                            {t.error_message && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setErrorDialog({ recipient: t.recipient_email, error: t.error_message })}
                                title="View error"
                                className="text-amber-700 hover:text-amber-800"
                              >
                                <AlertCircle className="w-4 h-4" />
                              </Button>
                            )}
                            {t.template_id && t.status === "failed" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRetry(t)}
                                disabled={retryingId === t.id}
                                title="Resend"
                                className="text-indigo-600 hover:text-indigo-700"
                              >
                                {retryingId === t.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>

        {/* Delivery timeline dialog */}
        <Dialog open={!!timelineDialog} onOpenChange={(open) => !open && setTimelineDialog(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Delivery timeline</DialogTitle>
              <DialogDescription>
                {timelineDialog?.sendRow?.recipient_email}{" "}
                {timelineDialog?.sendRow?.subject && (
                  <span className="text-slate-500">— {timelineDialog.sendRow.subject}</span>
                )}
              </DialogDescription>
            </DialogHeader>
            {timelineDialog?.meta && !timelineDialog.loading && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1.5">
                {timelineDialog.meta.template_name && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-slate-500 w-20 flex-shrink-0">Template</span>
                    <span className="text-slate-900 font-medium">{timelineDialog.meta.template_name}</span>
                    {timelineDialog.meta.category && (
                      <Badge variant="outline" className="text-xs">{timelineDialog.meta.category}</Badge>
                    )}
                    {timelineDialog.meta.is_test && (
                      <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">test</Badge>
                    )}
                  </div>
                )}
                {timelineDialog.meta.sequence && (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-slate-500 w-20 flex-shrink-0">Sequence</span>
                      {timelineDialog.meta.sequence.sequence_name ? (
                        <span className="text-slate-900 font-medium">
                          {timelineDialog.meta.sequence.sequence_name}
                          {timelineDialog.meta.sequence.deleted && (
                            <span className="ml-2 italic text-slate-400 text-xs">(deleted)</span>
                          )}
                        </span>
                      ) : (
                        <span className="italic text-slate-400">Deleted sequence</span>
                      )}
                    </div>
                    {timelineDialog.meta.sequence.step_position != null && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-slate-500 w-20 flex-shrink-0">Step</span>
                        <span className="text-slate-900">
                          {timelineDialog.meta.sequence.step_position}
                          {timelineDialog.meta.sequence.total_steps ? ` of ${timelineDialog.meta.sequence.total_steps}` : ""}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {timelineDialog.meta.from_address && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-slate-500 w-20 flex-shrink-0">From</span>
                    <span className="text-slate-700 font-mono">{timelineDialog.meta.from_address}</span>
                  </div>
                )}
                {timelineDialog.meta.hubspot_contact_id && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-slate-500 w-20 flex-shrink-0">HubSpot ID</span>
                    <span className="text-slate-700 font-mono">{timelineDialog.meta.hubspot_contact_id}</span>
                  </div>
                )}
              </div>
            )}
            {timelineDialog?.loading ? (
              <p className="text-slate-500 text-sm py-4">Loading…</p>
            ) : (timelineDialog?.events || []).length === 0 ? (
              <p className="text-slate-500 text-sm py-4">No events recorded.</p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-3 space-y-5 py-2">
                {(timelineDialog?.events || []).map((e, idx) => {
                  const colorMap = {
                    sent: "bg-emerald-100 text-emerald-700 ring-emerald-100",
                    delivered: "bg-emerald-200 text-emerald-800 ring-emerald-200",
                    delivery_delayed: "bg-amber-100 text-amber-700 ring-amber-100",
                    opened: "bg-blue-100 text-blue-700 ring-blue-100",
                    clicked: "bg-violet-100 text-violet-700 ring-violet-100",
                    bounced: "bg-red-100 text-red-700 ring-red-100",
                    complained: "bg-orange-100 text-orange-700 ring-orange-100",
                    send_failed: "bg-red-100 text-red-700 ring-red-100",
                  };
                  const labelMap = {
                    sent: "Sent",
                    send_failed: "Send failed",
                    delivered: "Delivered",
                    delivery_delayed: "Delivery delayed",
                    opened: "Opened",
                    clicked: "Clicked",
                    bounced: "Bounced",
                    complained: "Spam complaint",
                  };
                  return (
                    <li key={idx} className="ml-4">
                      <span className={`absolute -left-1.5 w-3 h-3 rounded-full ring-4 ${colorMap[e.type] || "bg-slate-200 text-slate-700 ring-slate-100"}`}></span>
                      <div className="flex items-baseline justify-between gap-3">
                        <Badge className={`${colorMap[e.type] || "bg-slate-100 text-slate-700"} hover:${colorMap[e.type] || "bg-slate-100 text-slate-700"}`}>
                          {labelMap[e.type] || e.type}
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono">
                          {e.at ? new Date(e.at).toLocaleString() : "—"}
                        </span>
                      </div>
                      {e.detail && (
                        <p className="text-xs text-slate-600 mt-1 break-words">{e.detail}</p>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
            <DialogFooter className="gap-2">
              {timelineDialog?.meta && (timelineDialog.meta.rendered_html || timelineDialog.meta.rendered_text) && (
                <Button
                  variant="outline"
                  onClick={() => setPreviewOpen(true)}
                  className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                >
                  <FileText className="w-4 h-4 mr-2" /> View email
                </Button>
              )}
              <Button variant="outline" onClick={() => setTimelineDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Email content preview sub-dialog. Sandboxed iframe so any inline
            styles render faithfully without giving the email JS access. */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Email preview</DialogTitle>
              <DialogDescription>
                {timelineDialog?.meta?.subject && (
                  <span className="text-slate-700">{timelineDialog.meta.subject}</span>
                )}
                <br />
                <span className="text-xs text-slate-500">
                  Sent to {timelineDialog?.sendRow?.recipient_email}
                </span>
              </DialogDescription>
            </DialogHeader>
            {timelineDialog?.meta?.rendered_html ? (
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={timelineDialog.meta.rendered_html}
                className="w-full h-[60vh] border border-slate-200 rounded-md bg-white"
              />
            ) : timelineDialog?.meta?.rendered_text ? (
              <pre className="w-full max-h-[60vh] overflow-auto whitespace-pre-wrap text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3">
                {timelineDialog.meta.rendered_text}
              </pre>
            ) : (
              <p className="text-sm text-slate-500 py-6 text-center">
                No saved copy of this email's content. Preview is only available for sends made after the snapshot feature was enabled.
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Error-detail dialog (failed sends) */}
        <Dialog open={!!errorDialog} onOpenChange={(open) => !open && setErrorDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" /> Send failed
              </DialogTitle>
              <DialogDescription>
                Recipient: <span className="font-mono">{errorDialog?.recipient}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label className="text-xs text-slate-500">Error from email provider</Label>
              <pre className="text-xs bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap break-words max-h-[300px] overflow-auto">
                {errorDialog?.error}
              </pre>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setErrorDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Enrol contact dialog */}
        <Dialog open={enrolDialogOpen} onOpenChange={setEnrolDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Enrol a contact in a sequence</DialogTitle>
              <DialogDescription>Pick an active sequence and a recipient. Contact must not be opted out or non-marketing in HubSpot. A contact whose earlier enrolment in the sequence was cancelled starts again at step 1.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sequence</Label>
                <Select value={enrolSequenceId} onValueChange={setEnrolSequenceId}>
                  <SelectTrigger><SelectValue placeholder="Pick an active sequence…" /></SelectTrigger>
                  <SelectContent>
                    {sequences.filter(s => s.status === "active").map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} <span className="text-slate-400 text-xs">· {s.steps?.length || 0} steps · {s.category}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Tabs value={enrolMode} onValueChange={setEnrolMode}>
                <TabsList>
                  <TabsTrigger value="hubspot">HubSpot contact</TabsTrigger>
                  <TabsTrigger value="manual">Manual entry</TabsTrigger>
                </TabsList>
                <TabsContent value="hubspot" className="space-y-3 mt-3">
                  <div className="flex gap-2">
                    <Select value={enrolListId} onValueChange={setEnrolListId}>
                      <SelectTrigger><SelectValue placeholder={hubspotLists.length ? "Pick a list…" : "No lists loaded"} /></SelectTrigger>
                      <SelectContent>
                        {hubspotLists.map((l) => (
                          <SelectItem key={l.list_id} value={l.list_id}>
                            {l.name} <span className="text-slate-400 text-xs ml-1">· {l.list_id}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleEnrolContactsLoad} variant="secondary" disabled={!enrolListId}>Load Contacts</Button>
                  </div>
                  {enrolContacts.length > 0 && (
                    <Select value={enrolSelectedContactId} onValueChange={setEnrolSelectedContactId}>
                      <SelectTrigger><SelectValue placeholder="Pick a contact…" /></SelectTrigger>
                      <SelectContent>
                        {enrolContacts.map((c) => {
                          const label = [c.email, c.firstname, c.company].filter(Boolean).join(" · ") || `(no email) ${c.hs_contact_id}`;
                          const disabled = c.opted_out || !c.marketable || !c.email;
                          return (
                            <SelectItem key={c.hs_contact_id} value={c.hs_contact_id} disabled={disabled}>
                              <span>{label}</span>
                              {c.opted_out && <Badge variant="outline" className="ml-2 text-red-600 border-red-300 text-xs">opted out</Badge>}
                              {!c.opted_out && !c.marketable && <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 text-xs">non-marketing</Badge>}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                  {enrolContacts.length > 0 && (
                    <p className="text-xs text-slate-500" data-testid="enrol-contacts-count">
                      {enrolTotalMembers > enrolContacts.length
                        ? `Showing ${enrolContacts.length.toLocaleString()} of ${enrolTotalMembers.toLocaleString()} list members`
                        : `${enrolContacts.length.toLocaleString()} list members`}
                    </p>
                  )}
                </TabsContent>
                <TabsContent value="manual" className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input type="email" value={enrolEmail} onChange={(e) => setEnrolEmail(e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input value={enrolName} onChange={(e) => setEnrolName(e.target.value)} />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEnrolDialogOpen(false)} disabled={enrolling}>Cancel</Button>
              <Button onClick={handleEnrolSubmit} disabled={enrolling} className="bg-indigo-600 hover:bg-indigo-700">
                {enrolling ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Enrolling…</> : "Enrol"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk enrol dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk enrol from a HubSpot list</DialogTitle>
              <DialogDescription>Enrols every sendable contact in the list (skips opt-outs, non-marketing, and contacts already in the sequence, including cancelled ones).</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Sequence</Label>
                <Select value={bulkSequenceId} onValueChange={setBulkSequenceId}>
                  <SelectTrigger><SelectValue placeholder="Pick an active sequence…" /></SelectTrigger>
                  <SelectContent>
                    {sequences.filter(s => s.status === "active").map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>HubSpot list</Label>
                <Select value={bulkListId} onValueChange={setBulkListId}>
                  <SelectTrigger><SelectValue placeholder={hubspotLists.length ? "Pick a list…" : "No lists loaded"} /></SelectTrigger>
                  <SelectContent>
                    {hubspotLists.map((l) => (
                      <SelectItem key={l.list_id} value={l.list_id}>
                        {l.name} <span className="text-slate-400 text-xs ml-1">· {l.list_id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulking}>Cancel</Button>
              <Button onClick={handleBulkSubmit} disabled={bulking} className="bg-indigo-600 hover:bg-indigo-700">
                {bulking ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Bulk enrolling…</> : "Bulk enrol"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmDialog
          open={!!retryEnrolment}
          onOpenChange={(open) => { if (!open) setRetryEnrolment(null); }}
          title="Retry this enrolment?"
          description={`${retryEnrolment?.recipient_email} resumes at the step that failed, which is sent again on the next scheduler run. Steps they already received won't be resent, and later steps whose send time passed while it was cancelled are skipped. Their HubSpot list membership isn't re-checked.`}
          confirmText="Retry"
          onConfirm={() => handleRetryEnrolment(retryEnrolment)}
        />

        <ConfirmDialog
          open={retryFailedOpen}
          onOpenChange={setRetryFailedOpen}
          title="Retry failed sends?"
          description={`Every enrolment in ${scopedSequence?.name || "this sequence"} that was cancelled because a send failed, or its template was deleted, resumes at the step that failed. Steps already received won't be resent, and later steps whose send time has passed are skipped. Contacts enrolled again since are skipped. HubSpot list membership isn't re-checked, so contacts who have left the list are retried too.`}
          confirmText="Retry failed sends"
          onConfirm={handleRetryFailed}
        />
      </main>
    </div>
  );
};

export default EmailTools;
